/**
 * The arithmetic behind the headquarters map on the Contact page.
 *
 * The map is plain OpenStreetMap raster tiles laid out around one point —
 * no map library, no API key, no script from a third party. OpenStreetMap's
 * tile policy allows this light use with a visible attribution, which the
 * map renders. Mirrors `xop-edge-customer/apps/portal-web/src/lib/osmTiles.ts`.
 */

export const OSM_TILE_SIZE = 256;

/**
 * The referrer policy every tile `<img>` carries. OpenStreetMap refuses a
 * browser tile request that arrives with NO Referer — it serves its striped
 * "Access blocked … 403" tile instead — and `index.html` sets a page-wide
 * `no-referrer` (it guards the invitation token in the accept URL). A
 * per-element policy overrides the page one, and `origin` sends only
 * `https://reseller.xopnetworks.com/`: enough for OSM, no path, no query.
 * Without it the whole map is "Access blocked" (2026-10-31).
 */
export const OSM_TILE_REFERRER_POLICY = "origin";

/** Fractional slippy-map tile coordinates of a point (Web Mercator). */
export function lonLatToTile(lat: number, lon: number, zoom: number): { x: number; y: number } {
  const n = 2 ** zoom;
  const latRad = (lat * Math.PI) / 180;
  return {
    x: ((lon + 180) / 360) * n,
    y: ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n,
  };
}

export interface PlacedTile {
  key: string;
  url: string;
  /** Pixel offset of the tile's top-left corner FROM THE POINT. */
  left: number;
  top: number;
}

/** The tiles `cols` either side and `rows` above/below the point's own tile,
 * each positioned relative to the point — so a container that puts its
 * origin at its centre shows the point dead centre. */
export function tilesAround(lat: number, lon: number, zoom: number, cols: number, rows: number): PlacedTile[] {
  const n = 2 ** zoom;
  const { x, y } = lonLatToTile(lat, lon, zoom);
  const cx = Math.floor(x);
  const cy = Math.floor(y);
  const tiles: PlacedTile[] = [];
  for (let dy = -rows; dy <= rows; dy += 1) {
    const ty = cy + dy;
    if (ty < 0 || ty >= n) continue;
    for (let dx = -cols; dx <= cols; dx += 1) {
      const tx = (((cx + dx) % n) + n) % n;
      tiles.push({
        key: `${zoom}/${tx}/${ty}`,
        url: `https://tile.openstreetmap.org/${zoom}/${tx}/${ty}.png`,
        left: (cx + dx - x) * OSM_TILE_SIZE,
        top: (ty - y) * OSM_TILE_SIZE,
      });
    }
  }
  return tiles;
}

/** A link that opens the same place, marked, on openstreetmap.org. */
export function osmPlaceUrl(lat: number, lon: number, zoom: number): string {
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=${zoom}/${lat}/${lon}`;
}
